import { Suspense, useCallback, useSyncExternalStore } from "react";
import { useWatchlists } from "@/domains/watchlists/hooks/useWatchlists";
import AppErrorBoundary from "@/shared/ui/AppErrorBoundary";
import LazyLoadErrorToast from "@/shared/ui/LazyLoadErrorToast";
import { lazyWithReload } from "@/shared/utils/lazyWithReload";

// Kept out of the entry chunk: most sessions never open this dialog.
const AddToWatchlistDialog = lazyWithReload(
  "AddToWatchlistDialog",
  () => import("@/shared/ui/watchlists/AddToWatchlistDialog"),
);

export interface AddToWatchlistDialogRequest {
  bondIds: string[];
}

interface AddToWatchlistDialogSnapshot {
  open: boolean;
  request: AddToWatchlistDialogRequest | null;
  // `_request` is cleared on close, so track first use separately: the dialog has
  // to stay mounted afterwards for the MUI close transition to play out.
  everOpened: boolean;
}

// Module-level state — same pattern as watchlistsStore / watchlistsService.
// The dialog is a singleton; only one can be open at a time.
// React Compiler does not treat these bindings as reactive, so the mount reads
// `_snapshot` through useSyncExternalStore. A useReducer tick left `open` stuck.
let _open = false;
let _request: AddToWatchlistDialogRequest | null = null;
let _resolve: ((watchlistId: string | null) => void) | null = null;
let _everOpened = false;
let _snapshot: AddToWatchlistDialogSnapshot = { open: false, request: null, everOpened: false };
const _listeners = new Set<() => void>();

function publish(): void {
  _snapshot = { open: _open, request: _request, everOpened: _everOpened };
  for (const listener of _listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

function getSnapshot(): AddToWatchlistDialogSnapshot {
  return _snapshot;
}

// Invoke before clearing. A local copy of this module binding is not a stable
// snapshot under the React compiler, so `const fn = _resolve; _resolve = null; fn()`
// calls null and the opener's promise never settles.
function settle(watchlistId: string | null): void {
  _resolve?.(watchlistId);
  _resolve = null;
}

export function openAddToWatchlistDialog(
  request: AddToWatchlistDialogRequest,
): Promise<string | null> {
  // Cancel any pending open before replacing it.
  settle(null);
  _open = true;
  _request = request;
  _everOpened = true;
  // Install the resolver before subscribers render. A render that closes the
  // dialog has to see this promise, not the nulled one from the previous open.
  const opened = new Promise<string | null>((resolve) => {
    _resolve = resolve;
  });
  publish();
  return opened;
}

/**
 * Renders the AddToWatchlist dialog and subscribes to module-level state.
 * Place this once inside AppProviders as a sibling to {children} — not a wrapper.
 * Callers open the dialog via `openAddToWatchlistDialog(...)` directly.
 */
export default function AddToWatchlistDialogMount() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // Read the list from the live store rather than a value frozen at open time:
  // a load that resolves after the dialog opens (or an optimistic create) then
  // shows up without the user having to reopen. `useWatchlists` also guarantees
  // the list is fetched on demand via `ensureLoaded`.
  const { data: watchlists } = useWatchlists();

  const handleClose = useCallback(() => {
    _open = false;
    _request = null;
    settle(null);
    publish();
  }, []);

  const handleConfirm = useCallback((watchlistId: string) => {
    _open = false;
    _request = null;
    settle(watchlistId);
    publish();
  }, []);

  // Never opened in this session: leave the dialog chunk unrequested.
  if (!snapshot.everOpened) {
    return null;
  }

  // This mount is a sibling of the whole app, so an uncaught failure here would
  // blank the page rather than just the dialog.
  return (
    <AppErrorBoundary
      fallback={
        <LazyLoadErrorToast message="Could not open Add to Watchlist. Reload the page to try again." />
      }
    >
      <Suspense fallback={null}>
        <AddToWatchlistDialog
          open={snapshot.open}
          watchlists={watchlists}
          bondIds={snapshot.request?.bondIds ?? []}
          onClose={handleClose}
          onConfirm={handleConfirm}
        />
      </Suspense>
    </AppErrorBoundary>
  );
}
